import { useState, useEffect, useRef } from "react";
import { supabase, canPersist } from "../lib/supabase";
import { migrateState } from "../engine/migrate";

const DEBOUNCE_MS  = 800;
const SNAPSHOT_CAP = 20;

/**
 * [T3] Autosave hook — writes to Supabase when configured, falls back to
 * localStorage for local-only / unauthenticated mode.
 *
 * @param {object}  st          Current DCEL state
 * @param {string}  projectId   UUID of the project row
 * @param {string}  userId      auth.uid() — null if not logged in
 * @returns {"saved"|"saving"|"unsaved"|"error"} saveStatus
 */
export function useSave(st, projectId, userId, plan = "free") {
  const [status, setStatus] = useState("saved");
  const timerRef = useRef(null);
  const prevRef  = useRef(null);

  useEffect(() => {
    if (prevRef.current === st) return;
    prevRef.current = st;

    setStatus("unsaved");
    clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      setStatus("saving");
      try {
        const isPro = plan === "pro" || plan === "team";
        if (canPersist && projectId && userId && isPro) {
          await saveToSupabase(st, projectId, userId);
        } else {
          await saveToLocalStorage(st, projectId);
        }
        setStatus("saved");
      } catch (err) {
        console.error("[useSave] Save failed:", err);
        setStatus("error");
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [st, projectId, userId]);

  return status;
}

async function saveToSupabase(st, projectId, userId) {
  const payload = {
    id:           projectId,
    owner_id:     userId,
    name:         st.project?.name    || "Untitled Project",
    client:       st.project?.client  || "",
    address:      st.project?.address || "",
    project_date: st.project?.date    || "",
    state:        st,
    updated_at:   new Date().toISOString(),
  };
  const { error } = await supabase
    .from("projects")
    .upsert(payload, { onConflict: "id" });
  if (error) throw error;

  await supabase.from("project_snapshots").insert({
    project_id: projectId,
    state:      st,
    label:      "Auto-save",
  });
}

// ── M4 FIX: localStorage size constants ──────────────────────────────────────
const LS_WARN_BYTES  = 2 * 1024 * 1024;  // warn at 2 MB
const LS_BLOCK_BYTES = 4 * 1024 * 1024;  // block at 4 MB (well under 5 MB DB cap)

async function saveToLocalStorage(st, projectId) {
  const key = `dpl-jade:${projectId || "default"}`;

  // Measure serialised size before writing
  const serialised = JSON.stringify(st);
  const byteSize   = new TextEncoder().encode(serialised).length;

  if (byteSize > LS_BLOCK_BYTES) {
    console.warn(`[useSave] State too large (${(byteSize/1024/1024).toFixed(1)}MB) — localStorage write blocked. Sign in to save to cloud.`);
    return; // silently skip rather than corrupt storage
  }

  if (byteSize > LS_WARN_BYTES) {
    console.warn(`[useSave] Project is getting large (${(byteSize/1024/1024).toFixed(1)}MB). Consider signing in to save to cloud.`);
  }

  try {
    localStorage.setItem(key, serialised);
    const snapKey  = `${key}:snapshots`;
    const existing = JSON.parse(localStorage.getItem(snapKey) || "[]");
    // Keep snapshot history small — only 5 copies locally (not 20) to save space
    localStorage.setItem(snapKey, JSON.stringify(
      [{ ts: Date.now(), state: st }, ...existing].slice(0, 5)
    ));
  } catch (e) {
    // QuotaExceededError — storage full; save just the current state, drop history
    try { localStorage.setItem(key, serialised); } catch { /* give up gracefully */ }
  }
}

/**
 * Clears all DPL Jade local data for a project (or all projects).
 * Called from the in-app Settings panel / "Clear local data" button.
 */
export function clearLocalData(projectId) {
  if (projectId) {
    const key = `dpl-jade:${projectId || "default"}`;
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}:snapshots`);
  } else {
    // Clear all jade keys
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith("dpl-jade:")) toRemove.push(k);
    }
    toRemove.forEach(k => localStorage.removeItem(k));
  }
}

/**
 * Loads the most recent saved state for a project.
 * Returns null if nothing is saved yet.
 */
export async function loadProject(projectId, userId) {
  if (canPersist && projectId && userId) {
    const { data, error } = await supabase
      .from("projects")
      .select("state")
      .eq("id", projectId)
      .eq("owner_id", userId)
      .single();
    if (error || !data) return null;
    return migrateState(data.state);
  }
  const raw = localStorage.getItem(`dpl-jade:${projectId || "default"}`);
  return raw ? migrateState(JSON.parse(raw)) : null;
}
